const ORG_COLLECTIONS = {
  tasks: 'opsTasks',
  projects: 'opsProjects',
  sections: 'opsSections',
  notes: 'opsNotes',
  notebooks: 'opsNotebooks',
  comments: 'opsComments',     // task comments — one doc each, so two people commenting never collide
  timeBlocks: 'opsTimeBlocks',
  milestones: 'opsMilestones',
  goals: 'opsGoals',
  departments: 'opsDepartments',
  projectTypes: 'opsProjectTypes',
  projectFiles: 'opsProjectFiles',
  media: 'opsMedia',              // every photo / voice note on a job, one doc each (shared/mediadoc.js): task, project, bid assessment — BattleBids writes here too
  weekPlans: 'opsWeekPlans',
  domains: 'opsDomains',          // Signo legacy, unused (departments replaced them)
  subdomains: 'opsSubdomains',
  folders: 'opsFolders',
  processes: 'opsProcesses',
  expenses: 'opsExpenses',
  vendors: 'opsVendors',
  accounts: 'opsAccounts',
  calendarEvents: 'opsCalendarEvents',
  clockSessions: 'opsSessions',
  timeEntries: 'opsTimeEntries',
  timeEdits: 'opsTimeEdits',        // the time ledger: every change to logged time (shared/timeledger)
  processExecutions: 'opsProcessRuns',
  taskFlow: 'opsFlow',
  suggestionEvents: 'opsSuggestionEvents',
  // Published by the books, read-only here (docs/SYNC.md §1).
  jobs: 'opsJobs',
  fieldReceipts: 'opsReceipts',
  customers: 'opsCustomers',
  members: 'opsMembers',          // the team (name, role, title, photo) — for assigning work
  teamStats: 'opsTeamStats',      // team numbers the books publish (time waiting for approval) — admins only
  jobPay: 'opsJobPay',            // one person's pay on one job (projectId__uid) — the add-person wizard
  pieces: 'opsPieces',            // approved / paid piece work, published by the books (pieceEntries)
  equipment: 'opsEquipment',      // the truck, big tools: cost pool + basis, published by the books
  tags: 'opsTags',                // the suite's one tag list (books tagVocab, by slug) — every member reads it
  taskTemplates: 'opsTaskTemplates',   // task templates (CFO 4.2) — owners write, everyone reads
  listTemplates: 'opsListTemplates',   // list templates (a task's pre-made list: groceries, a workout) — everyone reads, any member writes
  calls: 'opsCalls',              // phone calls matched to a customer / lead on the device (shared/calls.js) + per-day counts of the rest — yours (ownerUid)
  metrics: 'opsMetrics',          // business metric definitions (CFO 4.4) — owners write, everyone reads
  metricValues: 'opsMetricValues',   // business metric values — yours (ownerUid), or everyone's for an owner
  rates: 'opsRates',              // overhead pool, cash, costing policy the books publish — admins only
  cfo: 'opsCfo',                  // the CFO view the books publish (cash, profit, 12-week forecast, compliance, WIP, budgets, leads) — admins only
  weather: 'opsWeather',          // Environment Canada's forecast: now = the shop, p-{projectId} = a job's site (the books publish them, shared/weather.js) — everyone
  places: 'opsPlaces',            // the business's places: shop = { name, address, lat, lng } (Settings → Shop location) — everyone reads, owners write
  server: 'opsServer',            // the VPS's own numbers (memory, disks, load, uptime, DB, containers, backups, health; 24 h history) — the API writes, admins only
  sales: 'opsSales',              // the books' Sales feed (S4: leads, bids sent / won / lost, pipeline) — owners + the Sales department's people
  finance: 'opsFinance',          // the books' Finance feed (S4: invoices, collected, bank rec, receipts, filings, budgets) — owners + Finance's people
  pay: 'opsPay',                  // how each person is paid (type, cost rate, shadow rate) — your own, or everyone's for an admin
};

const REMOTE_TO_LOGICAL = {};
for (const k of Object.keys(ORG_COLLECTIONS)) REMOTE_TO_LOGICAL[ORG_COLLECTIONS[k]] = k;

const META_NAMES = ['clock', 'timer', 'lock', 'settings', 'focus', 'pushTrace', 'notesLock', 'deptLearn'];   // focus = phone focus rules (private); pushTrace = what the phone did with the last push; notesLock = the locked-notes keyring (shared/lock.js); deptLearn = word → department counts from your picks (deptinfer.js)
// Org collections whose docs may carry a lazy `chunks` subcollection (a note's body + revisions).
const WITH_CHUNKS = new Set(['notes']);

// Per-user (private) collections: meta docs, GPS tracks, screen time, reminders, personal counters.
export const LOGICAL_COLLECTIONS = [...Object.keys(ORG_COLLECTIONS), 'meta', 'dayTracks', 'phoneUsage', 'reminders', 'reminderAnswers', 'personalMetrics', 'personalMetricValues'];
// Flat per-user collections: logical name → the opsUsers/{uid}/{name} subcollection.
const USER_FLAT = { phoneUsage: 'phone', reminders: 'reminders', reminderAnswers: 'answers', personalMetrics: 'metrics', personalMetricValues: 'metricValues' };
const USER_FLAT_BACK = Object.fromEntries(Object.entries(USER_FLAT).map(([k, v]) => [v, k]));

function split(path) {
  if (typeof path !== 'string') throw new Error(`paths: expected a string path, got ${typeof path}`);
  const segs = path.split('/');
  if (!path || segs.some((s) => !s)) throw new Error(`paths: malformed path "${path}"`);
  return segs;
}

function need(ident) {
  if (!ident || !ident.orgId || !ident.uid) throw new Error('paths: orgId and uid are required');
}

// Accepts document paths and collection paths (`tasks`, `dayTracks/{date}/chunks`).
export function toRemote(logicalPath, ident) {
  need(ident);
  const segs = split(logicalPath);
  const [col, id, sub, subId] = segs;
  const org = `orgs/${ident.orgId}`;
  const user = `${org}/opsUsers/${ident.uid}`;

  if (ORG_COLLECTIONS[col]) {
    const chunked = WITH_CHUNKS.has(col) && segs.length <= 4 && (sub === undefined || sub === 'chunks');
    if (segs.length > 2 && !chunked) throw new Error(`paths: "${col}" has no subcollections ("${logicalPath}")`);
    return [org, ORG_COLLECTIONS[col], ...segs.slice(1)].join('/');
  }
  if (col === 'meta') {
    if (segs.length > 2) throw new Error(`paths: "meta" has no subcollections ("${logicalPath}")`);
    if (id !== undefined && !META_NAMES.includes(id)) throw new Error(`paths: unmapped meta doc "${id}"`);
    return [user, 'state', ...segs.slice(1)].join('/');
  }
  if (col === 'dayTracks') {
    if (segs.length > 4 || (sub !== undefined && sub !== 'chunks')) {
      throw new Error(`paths: unmapped dayTracks path "${logicalPath}"`);
    }
    return [user, 'tracks', ...segs.slice(1)].join('/');
  }
  if (USER_FLAT[col]) {   // screen time, alarms, ring answers, personal counters + their values — private to their person
    if (segs.length > 2) throw new Error(`paths: "${col}" has no subcollections ("${logicalPath}")`);
    return [user, USER_FLAT[col], ...segs.slice(1)].join('/');
  }
  throw new Error(`paths: unmapped logical collection "${col}"`);
}

// null = not visible to this identity (other org, other user's opsUsers, unknown collection).
export function toLogical(remotePath, ident) {
  need(ident);
  if (typeof remotePath !== 'string' || !remotePath) return null;
  const segs = remotePath.split('/');
  if (segs.some((s) => !s)) return null;
  if (segs[0] !== 'orgs' || segs[1] !== ident.orgId || segs.length < 3) return null;
  const rest = segs.slice(2);
  const col = rest[0];

  if (REMOTE_TO_LOGICAL[col]) {
    const chunked = WITH_CHUNKS.has(REMOTE_TO_LOGICAL[col]) && rest.length <= 4 && (rest.length <= 2 || rest[2] === 'chunks');
    if (rest.length > 2 && !chunked) return null;
    return [REMOTE_TO_LOGICAL[col], ...rest.slice(1)].join('/');
  }
  if (col !== 'opsUsers' || rest[1] !== ident.uid || rest.length < 3) return null;
  const kind = rest[2];
  const tail = rest.slice(3);
  if (kind === 'state') {
    if (tail.length > 1) return null;
    if (tail.length === 1 && !META_NAMES.includes(tail[0])) return null;
    return ['meta', ...tail].join('/');
  }
  if (kind === 'tracks') {
    if (tail.length > 3) return null;
    if (tail.length >= 2 && tail[1] !== 'chunks') return null;
    return ['dayTracks', ...tail].join('/');
  }
  if (USER_FLAT_BACK[kind]) {
    if (tail.length > 1) return null;
    return [USER_FLAT_BACK[kind], ...tail].join('/');
  }
  return null;
}
